import {
  editableFormPayload,
  type FormIssues,
  type GeneratedFormDefinition,
  type GeneratedFormValues,
} from '@template-ui/helpers/generated-form.helpers';
import {
  applyWorkflowFormConfiguration,
  type WorkflowFormConfiguration,
} from './workflow-form-configuration';
import type { WorkflowCaptureForm } from './workflow-capture-form';
import { resolveCaptureLookupFields, resolveProvidedLookupFields, type WorkflowFormLookupFields } from './workflow-form-lookup';
import {
  applyRuntimeFieldPolicy,
  policyAllowsOutcomes,
  fieldPolicyKey,
  policyEditableValues,
  policyInitialValues,
  type WorkflowRuntimeFieldPolicy,
  type WorkflowFieldPolicySource,
} from './workflow-runtime-field-policy';

import type {
  WorkflowClientCommandKey,
  WorkflowClientCommandReference,
} from './workflow-command-contracts';
export type {
  WorkflowClientCommandKey,
  WorkflowClientCommandReference,
} from './workflow-command-contracts';
export interface WorkflowClientAction {
  command: WorkflowClientCommandReference;
  form: {
    contract: string;
    version: number;
    inputSchemaHash: string;
    configuration?: WorkflowFormConfiguration | null;
    captureForm?: WorkflowCaptureForm | null;
    fieldPolicy?: WorkflowRuntimeFieldPolicy | null;
    initialValues?: GeneratedFormValues | null;
    readOnlyFieldIds?: string[] | null;
    lookupFields?: WorkflowFormLookupFields | null;
  } | null;
  decisionRecordId: number | null;
  outcomes: {
    code: string;
    label: string;
    reasonPolicy: string;
    reasonCapture?:
      | import('./workflow-reason-capture').WorkflowReasonCaptureContract
      | null;
    enabled?: boolean;
  }[];
}
export interface WorkflowCommandContext {
  workflowInstanceId: number;
  occurrenceId: number | null;
  stepETag: string | null;
}
export interface WorkflowClientInteraction {
  code: string;
  version: number;
  command: WorkflowClientCommandReference;
}

/** Trusted application code owns these bindings. A saved workflow cannot supply an endpoint or executable adapter. */
export interface WorkflowClientCommandAdapter {
  key: WorkflowClientCommandKey;
  form: GeneratedFormDefinition;
  endpointSuffix: string;
  submitLabel: string;
  submissionHint: string;
  confirmation: string;
  /** Installed adapters must explicitly implement the closed provided-lookup transport. */
  supportsProvidedLookups?: boolean;
  outcomeField?: string;
  outcomePrompt?: string;
  bindContext(
    context: WorkflowCommandContext,
    action: WorkflowClientAction,
  ): Record<string, unknown> | null;
  formForAction?(action: WorkflowClientAction): GeneratedFormDefinition | null;
  serializeInput?(
    form: GeneratedFormDefinition,
    values: GeneratedFormValues,
    action?: WorkflowClientAction,
  ): Record<string, unknown> | null;
  validateInput?(
    action: WorkflowClientAction,
    values: GeneratedFormValues,
  ): FormIssues;
  draftContextKey?(action: WorkflowClientAction): string | null;
}
export interface WorkflowClientCommandModule {
  code: string;
  version: number;
  commands: readonly WorkflowClientCommandAdapter[];
}
export interface WorkflowClientCommandBinding {
  adapter: WorkflowClientCommandAdapter;
  form: GeneratedFormDefinition;
  fieldPolicyKey?: string | null;
  fieldPolicySource?: WorkflowFieldPolicySource;
  lookupKey?: string | null;
  lookupIssues?: FormIssues;
  /** Exact provided command and declaration snapshot, separate from user values and transport metadata. */
  providedContextKey?: string;
}

function providedContextKey(action: WorkflowClientAction): string {
  return JSON.stringify([action.command, action.form?.contract, action.form?.version, action.form?.configuration ?? null]);
}

export function workflowCommandIdentity(key: WorkflowClientCommandKey): string {
  return JSON.stringify([key.provider, key.kind, key.code, key.version]);
}

export class WorkflowClientCommandRegistry {
  private readonly adapters = new Map<string, WorkflowClientCommandAdapter>();
  constructor(modules: readonly WorkflowClientCommandModule[]) {
    const installed = new Set<string>();
    for (const module of modules) {
      if (
        !module.code ||
        !Number.isSafeInteger(module.version) ||
        module.version < 1 ||
        installed.has(module.code)
      )
        throw new Error(
          'A client command module must have one unique installed identity.',
        );
      installed.add(module.code);
      for (const adapter of module.commands) {
        const key = adapter.key,
          identity = workflowCommandIdentity(key);
        if (
          !key.provider ||
          !key.code ||
          key.kind !== 'command' ||
          !Number.isSafeInteger(key.version) ||
          key.version < 1 ||
          this.adapters.has(identity)
        )
          throw new Error(
            'A client command must have one unique installed identity.',
          );
        if (
          !/^\/[a-zA-Z0-9_/-]+$/.test(adapter.endpointSuffix) ||
          adapter.endpointSuffix.startsWith('//')
        )
          throw new Error(
            'A client command endpoint must be a local workflow endpoint suffix.',
          );
        this.adapters.set(identity, adapter);
      }
    }
  }

  private compatibleAdapter(
    action: WorkflowClientAction,
    interaction?: WorkflowClientInteraction | null,
  ): WorkflowClientCommandAdapter | null {
    const reference = action.command;
    const adapter = this.adapters.get(workflowCommandIdentity(reference.key));
    if (
      !adapter ||
      !reference.inputSchemaHash ||
      !reference.contractHash ||
      action.form?.contract !== adapter.form.contract ||
      action.form.version !== adapter.form.version ||
      action.form.inputSchemaHash !== reference.inputSchemaHash
    )
      return null;
    if (
      interaction &&
      (interaction.code !== 'generated-command-form' ||
        interaction.version !== 1 ||
        workflowCommandIdentity(interaction.command.key) !==
          workflowCommandIdentity(reference.key) ||
        interaction.command.inputSchemaHash !== reference.inputSchemaHash ||
        interaction.command.outputSchemaHash !== reference.outputSchemaHash ||
        interaction.command.contractHash !== reference.contractHash)
    )
      return null;
    return adapter;
  }

  /** Display saved provided lookups without producing runtime authority, offered choices, or a command binding. */
  previewForm(
    action: WorkflowClientAction,
    interaction?: WorkflowClientInteraction | null,
  ): GeneratedFormDefinition | null {
    const adapter = this.compatibleAdapter(action, interaction);
    if (!adapter || adapter.supportsProvidedLookups !== true || !action.form || action.form.captureForm != null)
      return null;
    // Only declaration metadata reaches this projection. Runtime policy, answers and revisions are deliberately absent.
    const displayAction: WorkflowClientAction = {
      command: action.command,
      form: { contract: action.form.contract, version: action.form.version,
        inputSchemaHash: action.form.inputSchemaHash, configuration: action.form.configuration },
      decisionRecordId: null,
      outcomes: action.outcomes,
    };
    const supplied = adapter.formForAction ? adapter.formForAction(displayAction) : adapter.form;
    if (!supplied) return null;
    const configured = applyWorkflowFormConfiguration(supplied, action.form.configuration, {
      outcomes: action.outcomes, outcomeField: adapter.outcomeField, installedDefinition: adapter.form,
    });
    if (!configured || !action.form.configuration?.fields.some(field => field.lookup != null || field.optionSource === 'lookup'))
      return null;
    const locked = action.form.readOnlyFieldIds ?? [];
    if (!Array.isArray(locked) || locked.some(name => !configured.fields.some(field => field.name === name)))
      return null;
    return { ...configured, fields: configured.fields.map(field => ({ ...field, readOnly: true })) };
  }

  resolve(
    action: WorkflowClientAction,
    interaction?: WorkflowClientInteraction | null,
    source?: WorkflowFieldPolicySource,
  ): WorkflowClientCommandBinding | null {
    const adapter = this.compatibleAdapter(action, interaction);
    if (!adapter || !action.form) return null;
    const reference = action.command;
    const supplied = adapter.formForAction
      ? adapter.formForAction(action)
      : adapter.form;
    if (!supplied) return null;
    if (adapter.supportsProvidedLookups === true && action.form.captureForm != null) return null;
    const configured = applyWorkflowFormConfiguration(
      supplied,
      action.form.configuration,
      {
        outcomes: action.outcomes,
        outcomeField: adapter.outcomeField,
        installedDefinition: adapter.form,
        lookupRuntimeAvailable: adapter.supportsProvidedLookups === true,
      },
    );
    if (!configured) return null;
    const form = applyRuntimeFieldPolicy(
      configured,
      action.form.fieldPolicy,
      reference,
      source,
      action.outcomes
        .filter((outcome) => outcome.enabled === false)
        .map((outcome) => outcome.code),
    );
    if (
      !form ||
      !policyAllowsOutcomes(action.form.fieldPolicy, action.outcomes)
    )
      return null;
    const providedLookup = adapter.supportsProvidedLookups === true && action.form.configuration?.fields.some(field => field?.lookup != null || field?.optionSource === 'lookup');
    if (providedLookup && !action.form.fieldPolicy) return null;
    const lookupSource = providedLookup && !source && action.form.fieldPolicy ? {
      workflowInstanceId: action.form.fieldPolicy.context.workflowInstanceId,
      occurrenceId: action.form.fieldPolicy.context.occurrenceId,
      configurationVersionId: action.form.fieldPolicy.context.configurationVersionId,
      definitionVersionId: action.form.fieldPolicy.context.definitionVersionId,
      definitionHash: action.form.fieldPolicy.context.definitionHash,
    } : source;
    const lookups = adapter.supportsProvidedLookups === true
      ? resolveProvidedLookupFields(form, action.form.configuration, action.form.lookupFields, lookupSource)
      : resolveCaptureLookupFields(form, action.form.captureForm, action.form.lookupFields, source);
    if (!lookups) return null;
    const resolved = lookups.form;
    const locked = action.form.readOnlyFieldIds ?? [];
    if (
      locked.some((name) => !resolved.fields.some((field) => field.name === name))
    )
      return null;
    return {
      adapter,
      fieldPolicyKey: fieldPolicyKey(action.form.fieldPolicy),
      fieldPolicySource: lookupSource,
      lookupKey: lookups.key,
      lookupIssues: lookups.issues,
      ...(adapter.supportsProvidedLookups === true ? { providedContextKey: providedContextKey(action) } : {}),
      form: locked.length
        ? {
            ...resolved,
            fields: resolved.fields.map((field) =>
              locked.includes(field.name)
                ? { ...field, readOnly: true }
                : field,
            ),
          }
        : resolved,
    };
  }

  editableInput(
    identity: string,
    values: GeneratedFormValues,
    resolvedForm?: GeneratedFormDefinition | null,
  ): GeneratedFormValues {
    const adapter = this.adapters.get(identity);
    const fields =
      adapter &&
      resolvedForm?.contract === adapter.form.contract &&
      resolvedForm.version === adapter.form.version
        ? resolvedForm.fields
        : (adapter?.form.fields ?? []);
    return Object.fromEntries(
      Object.entries(values).filter(([key]) =>
        fields.some((field) => field.name === key && !field.readOnly),
      ),
    );
  }

  request(
    binding: WorkflowClientCommandBinding,
    action: WorkflowClientAction,
    context: WorkflowCommandContext,
    values: GeneratedFormValues,
    source?: WorkflowFieldPolicySource,
  ): { suffix: string; body: Record<string, unknown> } | null {
    if (
      !Number.isSafeInteger(context.workflowInstanceId) ||
      context.workflowInstanceId < 1 ||
      !Number.isSafeInteger(context.occurrenceId) ||
      context.occurrenceId! < 1 ||
      !context.stepETag
    )
      return null;
    if (!action.form) return null;
    if (binding.adapter.supportsProvidedLookups === true && binding.providedContextKey !== providedContextKey(action)) return null;
    if ((binding.fieldPolicyKey ?? null) !== fieldPolicyKey(action.form?.fieldPolicy)) return null;
    if (action.form?.fieldPolicy || action.form?.captureForm?.version === 3 || binding.lookupKey != null || binding.adapter.supportsProvidedLookups === true) {
      const refreshed = this.resolve(action, null, source ?? binding.fieldPolicySource);
      if (!refreshed) return null;
      if ((binding.lookupKey ?? null) !== (refreshed.lookupKey ?? null)) return null;
      const policy = action.form.fieldPolicy?.context;
      if (
        policy && (policy.workflowInstanceId !== context.workflowInstanceId ||
        policy.occurrenceId !== context.occurrenceId) ||
        binding.fieldPolicyKey !== fieldPolicyKey(action.form.fieldPolicy)
      )
        return null;
      binding = refreshed;
    }
    const owner = source ?? binding.fieldPolicySource;
    if (binding.lookupKey != null && owner && (owner.workflowInstanceId !== context.workflowInstanceId || owner.occurrenceId !== context.occurrenceId)) return null;
    const bound = binding.adapter.bindContext(context, action);
    const input = this.withBoundValues(action, values);
    const payload = action.form?.fieldPolicy
      ? policyEditableValues(binding.form, input)
      : input;
    const serialized = binding.adapter.serializeInput
      ? binding.adapter.serializeInput(binding.form, payload, action)
      : editableFormPayload(binding.form, payload);
    return bound === null || serialized === null
      ? null
      : {
          suffix: binding.adapter.endpointSuffix,
          body: {
            ...serialized,
            ...bound,
          },
        };
  }

  withBoundValues(
    action: WorkflowClientAction,
    values: GeneratedFormValues,
  ): GeneratedFormValues {
    const bound = action.form?.initialValues ?? {};
    if (action.form?.fieldPolicy) {
      const form = this.resolve(action)?.form;
      if (!form) return {};
      const initial = policyInitialValues(form, action.form.fieldPolicy, bound);
      return {
        ...policyEditableValues(form, values),
        ...Object.fromEntries(
          Object.entries(initial).filter(([name]) =>
            form.fields.some((field) => field.name === name && field.readOnly),
          ),
        ),
      };
    }
    return {
      ...values,
      ...Object.fromEntries(
        (action.form?.readOnlyFieldIds ?? [])
          .filter((name) => Object.hasOwn(bound, name))
          .map((name) => [name, bound[name]]),
      ),
    };
  }

  initialInput(
    action: WorkflowClientAction,
    draft: GeneratedFormValues,
  ): GeneratedFormValues {
    const form = this.resolve(action)?.form;
    const fields = form?.fields ?? [];
    if (action.form?.fieldPolicy)
      return form
        ? {
            ...policyInitialValues(
              form,
              action.form.fieldPolicy,
              action.form.initialValues ?? {},
            ),
            ...this.withBoundValues(action, policyEditableValues(form, draft)),
          }
        : {};
    const initial = Object.fromEntries(
      Object.entries(action.form?.initialValues ?? {}).filter(([name]) =>
        fields.some((field) => field.name === name),
      ),
    );
    return this.withBoundValues(action, { ...initial, ...draft });
  }
}
